/**
 * Page 3: one story and the reporting behind it.
 *
 * Opened from a feed row or straight from an email link, which is why the story
 * is scoped to its edition rather than to a public slug: "Back" has to return to
 * the right list, and the story has to belong to the subscriber who was sent it.
 */

import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import TrialShell from './TrialShell';
import TrialExpired from './TrialExpired';
import trialApi from './trialApi';
import { relativeTime, useFocusHeading, useTrialData } from './useTrialPage';
import { EVENTS, track } from '../../analytics/campaignAnalytics';

/** The handoff shows the first four, then a toggle. Hidden at four or fewer. */
const VISIBLE_SOURCES = 4;

function SourceRow({ source, onOpen }) {
  return (
    <div className="tr-source">
      <div className="tr-source-publisher">
        {source.publisher}
        {source.isPreferredPublisher && <span aria-label="Preferred publisher" title="Preferred publisher"> &#9733;</span>}
      </div>
      {source.headline && <p className="tr-source-headline">{source.headline}</p>}
      <div className="tr-source-date">{relativeTime(source.publishedAt)}</div>
      {source.url ? (
        <a
          className="tr-link"
          href={source.url}
          target="_blank"
          rel="noreferrer"
          onClick={() => onOpen(source)}
          style={{ display: 'inline-block', marginTop: 8 }}
        >
          Read original article&nbsp;&#8599;
        </a>
      ) : (
        /* The row stays. A publisher we cannot link to is still coverage, and
           dropping it would understate the one number this page exists to show. */
        <p className="tr-source-unavailable" style={{ marginTop: 8 }}>Article link unavailable</p>
      )}
    </div>
  );
}

export default function StoryPage() {
  const { token, storyId } = useParams();
  const [params] = useSearchParams();
  const { data, error, loading } = useTrialData(
    (cfg) => trialApi.getStory(token, storyId, cfg),
    [token, storyId],
  );
  const [expanded, setExpanded] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const heading = useFocusHeading(Boolean(data));

  useEffect(() => {
    if (!data) return;
    setFeedback(data.story.feedback);
    track(EVENTS.STORY_OPENED, { story_id: storyId, edition_id: data.edition?.id });
    // The email marks its own links, so an arrival from an inbox is
    // distinguishable from one via the feed without guessing from a referrer.
    if (params.get('from') === 'email') {
      track(EVENTS.EMAIL_STORY_CLICKED, { story_id: storyId, edition_id: data.edition?.id });
    }
  }, [data, storyId, params]);

  if (error?.code === 'TRIAL_LINK_EXPIRED' || error?.code === 'TRIAL_LINK_INVALID') {
    return <TrialExpired code={error.code} />;
  }

  const story = data?.story;
  const sources = story?.sources || [];
  const shown = expanded ? sources : sources.slice(0, VISIBLE_SOURCES);

  const setValue = async (next) => {
    const value = feedback === next ? null : next;
    setFeedback(value);
    try {
      const res = await trialApi.setFeedback(token, storyId, value);
      setFeedback(res.feedback);
      track(EVENTS.STORY_FEEDBACK_SUBMITTED, { story_id: storyId, value: value || 'cleared' });
    } catch {
      setFeedback(data.story.feedback);
    }
  };

  return (
    <TrialShell
      token={token}
      active="feed"
      onContactClick={() => track(EVENTS.CONTACT_CLICKED)}
      onSiteClick={() => track(EVENTS.SITE_LINK_CLICKED)}
    >
      <main className="tr-main">
        <div className="tr-wrap tr-wrap--story">
          <Link className="tr-back" to={`/trial/feed/${token}`}>
            <span aria-hidden="true">&#8592;</span> Back to my news feed
          </Link>

          {loading && <p className="tr-body" role="status">Loading this story</p>}
          {error && !loading && <p className="tr-error" role="alert">{error.message}</p>}

          {story && (
            <>
              <div style={{ marginTop: 16 }}>
                {story.category && <span className="tr-tag">{story.category}</span>}
              </div>
              <h1 className="tr-h1 tr-h1--large" tabIndex={-1} ref={heading} style={{ marginTop: 12 }}>
                {story.headline}
              </h1>
              <p className="tr-meta" style={{ marginTop: 12 }}>
                {/*
                  * "X articles from Y news sources" only when the two can be
                  * told apart, which the backend reports rather than assumes.
                  * Otherwise just the article count, per the handoff.
                  */}
                {story.publisherCount > 0
                  ? `${story.articleCount} article${story.articleCount === 1 ? '' : 's'} from ${story.publisherCount} news source${story.publisherCount === 1 ? '' : 's'}`
                  : `${story.articleCount} article${story.articleCount === 1 ? '' : 's'}`}
                {story.firstReportedAt ? ` · First reported ${relativeTime(story.firstReportedAt)}` : ''}
              </p>

              {/*
                * Feedback sits full width under the headline, out of both
                * columns. It is two small buttons and it is the one signal we
                * actively want; a narrow sidebar beside the draft offer would
                * bury it.
                */}
              <div className="tr-story-actions" style={{ marginTop: 20 }}>
                <div className="tr-feedback">
                  <button
                    type="button" className="tr-fb-btn tr-fb-btn--yes"
                    aria-pressed={feedback === 'relevant'} aria-label="Mark this story relevant"
                    onClick={() => setValue('relevant')}
                  >
                    <span aria-hidden="true">&#128077;</span> Relevant
                  </button>
                  <button
                    type="button" className="tr-fb-btn tr-fb-btn--no"
                    aria-pressed={feedback === 'not_relevant'} aria-label="Mark this story not relevant"
                    onClick={() => setValue('not_relevant')}
                  >
                    <span aria-hidden="true">&#128078;</span> Not relevant
                  </button>
                  {feedback && <span className="tr-fb-saved" role="status">Saved</span>}
                </div>
              </div>

              <div className="tr-story-layout" style={{ marginTop: 24 }}>
                {/* Sources lead and take the wider column: Adi, 1 Nov 2026,
                    "the sources are more important". */}
                <div className="tr-story-sources">
              <section className="tr-panel">
                <h2 className="tr-h2">News sources</h2>
                <p className="tr-body" style={{ marginTop: 8 }}>
                  Explore the original reporting and compare coverage from different sources.
                </p>
                <div style={{ marginTop: 12 }}>
                  {shown.map((s, i) => (
                    <SourceRow
                      key={`${s.url || s.publisher}-${i}`}
                      source={s}
                      onOpen={(src) => track(EVENTS.SOURCE_CLICKED, { story_id: storyId, publisher: src.publisher })}
                    />
                  ))}
                </div>
                {sources.length > VISIBLE_SOURCES && (
                  <button
                    type="button"
                    className="tr-btn tr-btn--secondary"
                    aria-expanded={expanded}
                    onClick={() => setExpanded((v) => !v)}
                    style={{ marginTop: 16 }}
                  >
                    {expanded ? 'Show fewer sources' : `Show all ${sources.length} sources`}
                  </button>
                )}
              </section>
                </div>

                <aside className="tr-story-offer">
                  <DraftPanel token={token} storyId={storyId} drafts={data.drafts} />
                </aside>
              </div>
            </>
          )}
        </div>
      </main>
    </TrialShell>
  );
}

/**
 * The AI draft invitation.
 *
 * When the allowance is spent the button is REMOVED rather than disabled, which
 * the handoff is specific about: a disabled control invites a click that cannot
 * work, and the rest of the page is unaffected by the limit.
 */
function DraftPanel({ token, storyId, drafts }) {
  const remaining = drafts?.remaining ?? 0;
  const allowance = drafts?.allowance ?? 0;
  const spent = remaining <= 0;

  return (
    <section className="tr-panel tr-card" style={{ marginTop: 32 }}>
      <p className="tr-overline">AI news draft</p>
      {spent ? (
        <>
          <h2 className="tr-h2" style={{ marginTop: 8 }}>
            You&rsquo;ve used the AI drafts included in your trial
          </h2>
          <p className="tr-body" style={{ marginTop: 8 }}>
            {`${allowance} of ${allowance} drafts used. Stories and sources stay fully available.`}
          </p>
        </>
      ) : (
        <>
          <h2 className="tr-h2" style={{ marginTop: 8 }}>
            Turn this story into a ready-to-edit news draft
          </h2>
          <p className="tr-body" style={{ marginTop: 8 }}>
            Alchemiq can turn the information from these sources into an AI-generated article
            draft in seconds. Review the text, check the linked sources, make your edits, and
            copy it when you&rsquo;re ready.
          </p>
          <Link
            className="tr-btn tr-btn--secondary"
            to={`/trial/feed/${token}/story/${storyId}/draft`}
            onClick={() => track(EVENTS.AI_DRAFT_REQUESTED, { story_id: storyId })}
            style={{ marginTop: 16 }}
          >
            Generate AI News Draft &#8594;
          </Link>
          <p className="tr-meta" style={{ marginTop: 12 }}>
            {`${remaining} of ${allowance} trial drafts left`}
          </p>
        </>
      )}
      <p className="tr-meta" style={{ marginTop: 12, fontStyle: 'italic' }}>
        AI-generated content should be reviewed and fact-checked before publication.
      </p>
    </section>
  );
}
